import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Heading, Select, Table, TableBody, TableCell, TableHeader, TableRow, Text, TextInput, Tip } from "grommet";
import { CaretDownFill, CaretUpFill, CircleInformation, Down, Link, Up } from "grommet-icons";
import { useUser } from "../../AuthContext";
import {
  fetchClaimedAtTimestamp,
  fetchFinishedSessionsUntilFound,
} from "./sessionHistoryApi";
import {
  appendFinishedSessions,
  AVERAGE_SETUP_TIME_TOOLTIP,
  calculateAverageSetupTime,
  calculateSessionStatistics,
  capitalizeDisplayName,
  CLAIMED_AT_FETCH_CONCURRENCY,
  DEFAULT_SESSIONS_PER_PAGE,
  displayOrMissing,
  FILTER_TYPE_FLAG,
  FILTER_TYPE_PROPERTY,
  FILTER_VALUE_ALL,
  formatClaimedAt,
  formatOrganizationDisplay,
  formatSessionDate,
  formatSessionTime,
  formatShowingCounter,
  FLAG_COLUMN_WIDTH,
  getFilteredSessions,
  getFlagFilterOptions,
  getClearedDynamicFilterState,
  getExpandedCardBackgroundColor,
  getFlagBackgroundColor,
  getHistoryErrorKind,
  getHistoryViewState,
  getNextSortDirection,
  getProctorAliases,
  getPropertyFilterOptions,
  getSessionsMissingClaimedAt,
  getSortColumn,
  getSortHeaderA11yTitle,
  getStatisticsTitlePrefix,
  getVisiblePageCount,
  isDefaultDynamicFilter,
  isSortAscending,
  MISSING_VALUE,
  nextFilterTypeState,
  paginateSessions,
  resolveExpandedSessionId,
  sortSessions,
  runWithConcurrency,
  SESSION_PAGE_SIZE_OPTIONS,
  shouldApplyClaimedAtResult,
  SORT_COLUMN_CANDIDATE,
  SORT_COLUMN_DATE,
  SORT_COLUMN_ORGANIZATION,
  SORT_NEWEST,
} from "./sessionHistoryUtils";

const SHOW_SESSION_STATISTICS_CARD = false;

const EXPANDED_SHADOW = "0 2px 10px rgba(0, 0, 0, 0.18)";
const EXPAND_COLUMN_WIDTH = "2.25rem";
const SORT_ICON_SIZE = "12px";
const SUMMARY_GRID_COLUMNS =
  "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto";
const EXPANDED_GRID_COLUMNS =
  "minmax(0, 20fr) minmax(0, 41fr) minmax(0, 13fr) minmax(0, 13fr) minmax(0, 13fr)";
const SESSION_ID_GRID_COLUMNS = "minmax(0, 75fr) minmax(0, 25fr)";
const GRID_COLUMN_GAP = "12px";

const FILTER_TYPE_OPTIONS = [
  { label: "Property", value: FILTER_TYPE_PROPERTY },
  { label: "Flag", value: FILTER_TYPE_FLAG },
];

const PAGE_SIZE_OPTIONS = SESSION_PAGE_SIZE_OPTIONS.map((size) => ({
  label: String(size),
  value: size,
}));

function TruncatedText({ value, size = "small", weight, textAlign }) {
  const source = value == null ? "" : String(value);
  const text = displayOrMissing(source);

  return (
    <Text
      size={size}
      weight={weight}
      truncate
      textAlign={textAlign}
      title={text === MISSING_VALUE ? undefined : text}
    >
      {text}
    </Text>
  );
}

function SortHeaderButton({
  label,
  column,
  align = "start",
  sortDirection,
  onSort,
}) {
  const isActive = getSortColumn(sortDirection) === column;
  const showAscending = isActive
    ? isSortAscending(sortDirection)
    : column !== SORT_COLUMN_DATE;
  const Icon = showAscending ? CaretUpFill : CaretDownFill;

  return (
    <Button
      plain
      fill="horizontal"
      a11yTitle={getSortHeaderA11yTitle(column, sortDirection)}
      aria-pressed={isActive}
      aria-sort={
        isActive ? (showAscending ? "ascending" : "descending") : "none"
      }
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onSort(column);
      }}
    >
      <Box
        direction="row"
        align="center"
        justify={align === "end" ? "end" : "start"}
        gap="xsmall"
        overflow="hidden"
        wrap={false}
      >
        <Text size="xsmall" weight="bold" color="text-weak" truncate>
          {label}
        </Text>
        <Box
          flex={false}
          width={SORT_ICON_SIZE}
          height={SORT_ICON_SIZE}
          align="center"
          justify="center"
        >
          <Icon
            size={SORT_ICON_SIZE}
            color={isActive ? "dark-1" : "light-4"}
          />
        </Box>
      </Box>
    </Button>
  );
}

function StatisticsHeaderCell({ label, wrap = false }) {
  return (
    <TableCell
      scope="col"
      pad={{ horizontal: "xsmall", vertical: "xxsmall" }}
      style={wrap ? { whiteSpace: "pre-line", maxWidth: "7.5rem" } : { whiteSpace: "nowrap" }}
    >
      <Text size="xsmall" color="text-weak">
        {label}
      </Text>
    </TableCell>
  );
}

function AverageSetupTimeHeader() {
  return (
    <TableCell
      scope="col"
      pad={{ horizontal: "xsmall", vertical: "xxsmall" }}
      style={{ whiteSpace: "nowrap" }}
    >
      <Box direction="row" align="center" gap="xxsmall" wrap={false}>
        <Text size="xsmall" color="text-weak">
          Average Setup Time
        </Text>
        <Tip
          content={
            <Box pad={{ horizontal: "small", vertical: "xsmall" }} width={{ max: "18rem" }}>
              <Text size="small">{AVERAGE_SETUP_TIME_TOOLTIP}</Text>
            </Box>
          }
          dropProps={{
            align: { top: "bottom" },
            stretch: false,
            trapFocus: false,
          }}
        >
          <Button
            plain
            icon={<CircleInformation size="12px" color="text-weak" />}
            a11yTitle="About average setup time"
          />
        </Tip>
      </Box>
    </TableCell>
  );
}

function StatisticsValueCell({ children, align = "start" }) {
  return (
    <TableCell
      pad={{ horizontal: "xsmall", vertical: "xxsmall" }}
      align={align}
      style={{ whiteSpace: "nowrap" }}
    >
      {children}
    </TableCell>
  );
}

function SessionStatisticsTable({
  filterType,
  filterValue,
  statistics,
  setupDisplay,
  setupCoverage,
  isSetupCalculating,
  hasMore,
}) {
  const statisticsTitlePrefix = getStatisticsTitlePrefix(filterType, filterValue);

  return (
    <Box
      gap="xsmall"
      fill="horizontal"
      margin={{ vertical: "small" }}
      pad="small"
      round="10px"
      border={{ color: "border", size: "xsmall" }}
      overflow="visible"
    >
      <Text size="small" color="dark-3">
        <Text size="small" color="dark-3" weight="bold">
          {statisticsTitlePrefix}
        </Text>
        {" "}
        <Text size="small" color="dark-3">
          Session Statistics
        </Text>
      </Text>
      <Box
        overflow={{ horizontal: "auto", vertical: "visible" }}
        fill="horizontal"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <StatisticsHeaderCell
                label={"Total Sessions\nSubmitted"}
                wrap
              />
              <StatisticsHeaderCell label="Green Sessions" />
              <StatisticsHeaderCell label="Yellow Sessions" />
              <StatisticsHeaderCell label="Orange Sessions" />
              <StatisticsHeaderCell label="Red Sessions" />
              <StatisticsHeaderCell label="DNS" />
              <StatisticsHeaderCell label="DNC / MOD / RECON" />
              <StatisticsHeaderCell label="Other / Unclassified" />
              <AverageSetupTimeHeader />
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <StatisticsValueCell>
                <Text size="small" weight={500}>
                  {statistics.total}
                </Text>
              </StatisticsValueCell>
              <StatisticsValueCell>
                <Text size="small" weight={500}>
                  {statistics.greenDisplay}
                </Text>
              </StatisticsValueCell>
              <StatisticsValueCell>
                <Text size="small" weight={500}>
                  {statistics.yellowDisplay}
                </Text>
              </StatisticsValueCell>
              <StatisticsValueCell>
                <Text size="small" weight={500}>
                  {statistics.orangeDisplay}
                </Text>
              </StatisticsValueCell>
              <StatisticsValueCell>
                <Text size="small" weight={500}>
                  {statistics.redDisplay}
                </Text>
              </StatisticsValueCell>
              <StatisticsValueCell>
                <Text size="small" weight={500}>
                  {statistics.dns}
                </Text>
              </StatisticsValueCell>
              <StatisticsValueCell>
                <Text size="small" weight={500}>
                  {statistics.dncGroup}
                </Text>
              </StatisticsValueCell>
              <StatisticsValueCell>
                <Text size="small" weight={500}>
                  {statistics.otherDisplay}
                </Text>
              </StatisticsValueCell>
              <StatisticsValueCell>
                <Box>
                  <Text size="small" weight={500}>
                    {isSetupCalculating ? "Calculating…" : setupDisplay}
                  </Text>
                  {!isSetupCalculating && (
                    <Text size="xsmall" color="text-weak">
                      {setupCoverage}
                    </Text>
                  )}
                </Box>
              </StatisticsValueCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      {hasMore && (
        <Text size="xsmall" color="text-weak">
          Statistics reflect loaded sessions. Load older sessions to include more
          history.
        </Text>
      )}
    </Box>
  );
}

function FlagCell({ flag }) {
  const text = displayOrMissing(flag);

  return (
    <Box
      flex={false}
      align="center"
      justify="center"
      overflow="hidden"
      style={{
        width: FLAG_COLUMN_WIDTH,
        minWidth: FLAG_COLUMN_WIDTH,
        maxWidth: FLAG_COLUMN_WIDTH,
        alignSelf: "stretch",
        backgroundColor: getFlagBackgroundColor(flag),
        borderRadius: 0,
        boxShadow: "none",
        opacity: 1,
      }}
    >
      <Text
        size="small"
        truncate
        textAlign="center"
        title={text === MISSING_VALUE ? undefined : text}
        style={{
          color: "#ffffff",
          fontWeight: 400,
          width: "100%",
          paddingLeft: 4,
          paddingRight: 4,
        }}
      >
        {text}
      </Text>
    </Box>
  );
}

function SummaryColumns({ session, expandControl }) {
  const candidateDisplay = capitalizeDisplayName(session.candidateName);
  const organizationDisplay = formatOrganizationDisplay(session.organizationId);

  return (
    <Box
      fill="horizontal"
      overflow="hidden"
      style={{
        display: "grid",
        gridTemplateColumns: SUMMARY_GRID_COLUMNS,
        columnGap: GRID_COLUMN_GAP,
        width: "100%",
        minWidth: 0,
        alignItems: "stretch",
      }}
    >
      <Box overflow="hidden" justify="center" pad={{ vertical: "xsmall" }}>
        <TruncatedText value={candidateDisplay} weight="bold" />
      </Box>
      <Box overflow="hidden" justify="center" pad={{ vertical: "xsmall" }}>
        <TruncatedText value={organizationDisplay} />
      </Box>
      <Box overflow="hidden" justify="center" pad={{ vertical: "xsmall" }}>
        <Text
          size="small"
          truncate
          textAlign="end"
          title={formatSessionDate(session)}
          style={{ whiteSpace: "nowrap" }}
        >
          {formatSessionDate(session)}
        </Text>
      </Box>
      <Box
        direction="row"
        justify="end"
        align="stretch"
        overflow="hidden"
        style={{ minWidth: 0 }}
      >
        <FlagCell flag={session.flag} />
      </Box>
      <Box
        flex={false}
        width={EXPAND_COLUMN_WIDTH}
        align="center"
        justify="center"
        pad={{ vertical: "xsmall" }}
      >
        {expandControl}
      </Box>
    </Box>
  );
}

function MetaField({ label, value, displayValue, align = "start" }) {
  const source = value == null ? "" : String(value);
  const text = displayValue || displayOrMissing(source);

  return (
    <Box gap="xxsmall" overflow="hidden" style={{ minWidth: 0 }}>
      <Text
        size="xsmall"
        color="text-weak"
        truncate
        textAlign={align}
        title={label}
      >
        {label}
      </Text>
      <Text
        size="small"
        truncate
        textAlign={align}
        title={text === MISSING_VALUE ? undefined : text}
        style={{ minWidth: 0 }}
      >
        {text}
      </Text>
    </Box>
  );
}

function ExpandedMeta({ session, claimedAt }) {
  const proctorDisplay = capitalizeDisplayName(session.proctorName);

  return (
    <Box
      fill="horizontal"
      overflow="hidden"
      style={{
        display: "grid",
        gridTemplateColumns: EXPANDED_GRID_COLUMNS,
        columnGap: GRID_COLUMN_GAP,
        width: "100%",
        minWidth: 0,
        alignItems: "start",
      }}
    >
      <MetaField
        label="Proctor Name"
        value={session.proctorName}
        displayValue={proctorDisplay || MISSING_VALUE}
      />
      <MetaField label="Exam Name" value={session.examName} />
      <MetaField label="Claimed At" value={claimedAt} align="end" />
      <MetaField
        label="Start Time"
        value={formatSessionTime(session.startedAt)}
        align="end"
      />
      <MetaField
        label="End Time"
        value={formatSessionTime(session.completedAt)}
        align="end"
      />
    </Box>
  );
}

function SessionHistory() {
  const { user, proctorName } = useUser();
  const email = user?.attributes?.email || "";
  const aliases = useMemo(
    () => getProctorAliases(proctorName, email),
    [proctorName, email],
  );
  const aliasesKey = aliases.join("\0");

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filterType, setFilterType] = useState(FILTER_TYPE_PROPERTY);
  const [filterValue, setFilterValue] = useState(FILTER_VALUE_ALL);
  const [sortDirection, setSortDirection] = useState(SORT_NEWEST);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [sessionsPerPage, setSessionsPerPage] = useState(
    DEFAULT_SESSIONS_PER_PAGE,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [claimedAtById, setClaimedAtById] = useState({});
  const [claimedAtLoadingId, setClaimedAtLoadingId] = useState(null);

  const generationRef = useRef(0);
  const tokensRef = useRef({});
  const claimedAtCacheRef = useRef({});
  const claimedAtInFlightRef = useRef(new Map());
  const claimedAtEpochRef = useRef(0);
  const statsGenerationRef = useRef(0);
  const aliasesRef = useRef(aliases);
  const sessionsRef = useRef(sessions);
  const lastSearchRef = useRef({
    aliasCount: 0,
    pagesEvaluated: 0,
    finishedCount: 0,
    aliasesWithToken: 0,
    aliasesExhausted: 0,
    errorKind: null,
  });

  aliasesRef.current = aliases;
  sessionsRef.current = sessions;

  const updateHasMore = useCallback(() => {
    setHasMore(
      aliasesRef.current.some((alias) => Boolean(tokensRef.current[alias])),
    );
  }, []);

  const loadSessions = useCallback(
    async ({ reset }) => {
      const currentAliases = aliasesRef.current;
      const generation = generationRef.current;

      if (!currentAliases.length) {
        setSessions([]);
        setHasMore(false);
        setLoading(false);
        setSearching(false);
        setLoadingMore(false);
        lastSearchRef.current = {
          aliasCount: 0,
          pagesEvaluated: 0,
          finishedCount: 0,
          aliasesWithToken: 0,
          aliasesExhausted: 0,
          errorKind: null,
        };
        return;
      }

      const aliasesToFetch = reset
        ? currentAliases
        : currentAliases.filter((alias) => tokensRef.current[alias]);

      if (!aliasesToFetch.length) {
        setLoading(false);
        setSearching(false);
        setLoadingMore(false);
        updateHasMore();
        return;
      }

      if (reset) {
        setLoading(true);
        setSearching(false);
        setError(false);
      } else {
        setLoadingMore(true);
        setLoadMoreError(false);
      }

      try {
        const results = await Promise.all(
          aliasesToFetch.map((alias) =>
            fetchFinishedSessionsUntilFound(
              alias,
              reset ? null : tokensRef.current[alias],
              {
                onSkippedEmptyPage: () => {
                  if (generation !== generationRef.current) {
                    return;
                  }

                  if (sessionsRef.current.length === 0) {
                    setSearching(true);
                    setLoading(false);
                  }
                },
              },
            ),
          ),
        );

        if (generation !== generationRef.current) {
          return;
        }

        results.forEach((result, index) => {
          tokensRef.current[aliasesToFetch[index]] = result.nextToken;
        });

        const incoming = results.flatMap((result) => result.sessions);
        setSessions((current) =>
          reset
            ? appendFinishedSessions([], incoming)
            : appendFinishedSessions(current, incoming),
        );
        updateHasMore();
        setError(false);
        setLoadMoreError(false);
        lastSearchRef.current = {
          aliasCount: currentAliases.length,
          pagesEvaluated: results.reduce(
            (sum, result) => sum + result.pagesEvaluated,
            0,
          ),
          finishedCount: incoming.length,
          aliasesWithToken: currentAliases.filter(
            (alias) => Boolean(tokensRef.current[alias]),
          ).length,
          aliasesExhausted: currentAliases.filter(
            (alias) => !tokensRef.current[alias],
          ).length,
          errorKind: null,
        };
      } catch (loadError) {
        if (generation !== generationRef.current) {
          return;
        }

        lastSearchRef.current = {
          ...lastSearchRef.current,
          aliasCount: currentAliases.length,
          errorKind: getHistoryErrorKind(loadError),
        };

        if (reset) {
          setError(true);
          setHasMore(false);
        } else {
          setLoadMoreError(true);
        }
      } finally {
        if (generation === generationRef.current) {
          setLoading(false);
          setSearching(false);
          setLoadingMore(false);
        }
      }
    },
    [updateHasMore],
  );

  useEffect(() => {
    if (!user) {
      setLoading(true);
      return;
    }

    generationRef.current += 1;
    statsGenerationRef.current += 1;
    claimedAtEpochRef.current += 1;
    tokensRef.current = Object.fromEntries(
      aliases.map((alias) => [alias, null]),
    );
    claimedAtCacheRef.current = {};
    claimedAtInFlightRef.current.clear();
    setClaimedAtById({});
    setClaimedAtLoadingId(null);
    setExpandedId(null);
    setFilterType(FILTER_TYPE_PROPERTY);
    setFilterValue(FILTER_VALUE_ALL);
    setSortDirection(SORT_NEWEST);
    setDateStart("");
    setDateEnd("");
    setSessionsPerPage(DEFAULT_SESSIONS_PER_PAGE);
    setCurrentPage(1);
    setLoadMoreError(false);
    setSearching(false);
    setSessions([]);

    loadSessions({ reset: true });
  }, [aliasesKey, aliases, loadSessions, user]);

  const propertyOptions = useMemo(
    () => getPropertyFilterOptions(sessions),
    [sessions],
  );
  const flagOptions = useMemo(
    () => getFlagFilterOptions(sessions),
    [sessions],
  );
  const valueOptions =
    filterType === FILTER_TYPE_FLAG ? flagOptions : propertyOptions;

  const filteredSessions = useMemo(
    () =>
      getFilteredSessions(sessions, {
        dateStart,
        dateEnd,
        filterType,
        filterValue,
      }),
    [sessions, dateStart, dateEnd, filterType, filterValue],
  );

  const filteredAndSortedSessions = useMemo(
    () => sortSessions(filteredSessions, sortDirection),
    [filteredSessions, sortDirection],
  );

  const sessionStatistics = useMemo(
    () => calculateSessionStatistics(filteredSessions),
    [filteredSessions],
  );

  const setupTimeStats = useMemo(
    () => calculateAverageSetupTime(filteredSessions, claimedAtById),
    [filteredSessions, claimedAtById],
  );

  const missingClaimedAtSessions = useMemo(
    () => getSessionsMissingClaimedAt(filteredSessions, claimedAtById),
    [filteredSessions, claimedAtById],
  );

  const isSetupCalculating = missingClaimedAtSessions.length > 0;

  const pagedResult = useMemo(
    () =>
      paginateSessions(
        filteredAndSortedSessions,
        currentPage,
        sessionsPerPage,
      ),
    [filteredAndSortedSessions, currentPage, sessionsPerPage],
  );

  const visibleSessions = pagedResult.visibleSessions;
  const pageCount = getVisiblePageCount(
    filteredAndSortedSessions.length,
    sessionsPerPage,
  );
  const safePage = pagedResult.page;

  useEffect(() => {
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  }, [safePage, currentPage]);

  useEffect(() => {
    setExpandedId((current) =>
      resolveExpandedSessionId(current, visibleSessions),
    );
  }, [visibleSessions]);

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const handleFilterTypeChange = (value) => {
    const next = nextFilterTypeState(value);
    setFilterType(next.filterType);
    setFilterValue(next.filterValue);
    goToFirstPage();
  };

  const handleFilterValueChange = (value) => {
    setFilterValue(value);
    goToFirstPage();
  };

  const handleHeaderSort = (column) => {
    setSortDirection((current) => getNextSortDirection(current, column));
    goToFirstPage();
  };

  const handleDateStartChange = (value) => {
    setDateStart(value);
    goToFirstPage();
  };

  const handleDateEndChange = (value) => {
    setDateEnd(value);
    goToFirstPage();
  };

  const handleClearDates = () => {
    setDateStart("");
    setDateEnd("");
    goToFirstPage();
  };

  const handleClearFilters = () => {
    const next = getClearedDynamicFilterState();
    setFilterType(next.filterType);
    setFilterValue(next.filterValue);
    setExpandedId(null);
    goToFirstPage();
  };

  const handleSessionsPerPageChange = (value) => {
    setSessionsPerPage(Number(value) || DEFAULT_SESSIONS_PER_PAGE);
    goToFirstPage();
  };

  const showingText = formatShowingCounter({
    page: safePage,
    sessionsPerPage,
    filteredCount: filteredAndSortedSessions.length,
    hasMore,
  });

  const hasClientFilters =
    filterValue !== FILTER_VALUE_ALL || Boolean(dateStart) || Boolean(dateEnd);

  const loadClaimedAt = useCallback((session) => {
    const id = session?.podSessionId;
    if (!id) {
      return Promise.resolve(null);
    }

    if (Object.prototype.hasOwnProperty.call(claimedAtCacheRef.current, id)) {
      return Promise.resolve(claimedAtCacheRef.current[id]);
    }

    const existing = claimedAtInFlightRef.current.get(id);
    if (existing) {
      return existing;
    }

    const epoch = claimedAtEpochRef.current;
    const pending = fetchClaimedAtTimestamp(id, session.proctorName)
      .catch(() => null)
      .then((timestamp) => {
        if (epoch === claimedAtEpochRef.current) {
          claimedAtCacheRef.current[id] = timestamp;
          setClaimedAtById((current) =>
            Object.prototype.hasOwnProperty.call(current, id)
              ? current
              : { ...current, [id]: timestamp },
          );
        }
        return timestamp;
      })
      .finally(() => {
        claimedAtInFlightRef.current.delete(id);
      });

    claimedAtInFlightRef.current.set(id, pending);
    return pending;
  }, []);

  useEffect(() => {
    const missing = getSessionsMissingClaimedAt(
      filteredSessions,
      claimedAtCacheRef.current,
    );
    if (!missing.length) {
      return undefined;
    }

    const generation = statsGenerationRef.current + 1;
    statsGenerationRef.current = generation;
    let cancelled = false;

    runWithConcurrency(
      missing,
      CLAIMED_AT_FETCH_CONCURRENCY,
      async (session) => {
        await loadClaimedAt(session);
      },
      () =>
        !cancelled &&
        shouldApplyClaimedAtResult(statsGenerationRef.current, generation),
    ).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [filteredSessions, loadClaimedAt]);

  const handleToggleExpanded = async (session) => {
    if (expandedId === session.podSessionId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(session.podSessionId);

    if (
      Object.prototype.hasOwnProperty.call(
        claimedAtCacheRef.current,
        session.podSessionId,
      )
    ) {
      return;
    }

    setClaimedAtLoadingId(session.podSessionId);

    try {
      await loadClaimedAt(session);
    } finally {
      setClaimedAtLoadingId((current) =>
        current === session.podSessionId ? null : current,
      );
    }
  };

  const claimedAtValue = (session) => {
    if (claimedAtLoadingId === session.podSessionId) {
      return "Loading...";
    }

    if (
      !Object.prototype.hasOwnProperty.call(
        claimedAtCacheRef.current,
        session.podSessionId,
      )
    ) {
      return MISSING_VALUE;
    }

    return formatClaimedAt(claimedAtById[session.podSessionId]);
  };

  const renderExpandButton = (session) => {
    const isExpanded = expandedId === session.podSessionId;

    return (
      <Button
        plain
        icon={isExpanded ? <Up size="small" /> : <Down size="small" />}
        a11yTitle={
          isExpanded
            ? `Collapse session ${session.podSessionId}`
            : `Expand session ${session.podSessionId}`
        }
        aria-expanded={isExpanded}
        onClick={() => handleToggleExpanded(session)}
      />
    );
  };

  const renderBody = () => {
    const viewState = getHistoryViewState({
      loading,
      searching,
      error,
      sessions,
      hasMore,
    });

    if (viewState === "loading") {
      return (
        <Text color="text-weak" textAlign="center">
          Loading session history...
        </Text>
      );
    }

    if (viewState === "searching") {
      return (
        <Text color="text-weak" textAlign="center">
          Searching completed sessions...
        </Text>
      );
    }

    if (viewState === "error") {
      return (
        <Box align="center" gap="small">
          <Text color="status-critical" textAlign="center">
            Unable to load session history. Please try again.
          </Text>
          <Button
            label="Try Again"
            onClick={() => loadSessions({ reset: true })}
          />
        </Box>
      );
    }

    if (viewState === "exhausted") {
      return (
        <Text color="text-weak" textAlign="center">
          No completed sessions found.
        </Text>
      );
    }

    if (!filteredAndSortedSessions.length && hasClientFilters) {
      return (
        <Text color="text-weak" textAlign="center">
          No completed sessions match these filters.
        </Text>
      );
    }

    if (viewState === "recent-empty") {
      return (
        <Text color="text-weak" textAlign="center">
          No completed sessions found in the recent pages.
        </Text>
      );
    }

    return (
      <Box fill="horizontal" overflow="hidden">
        <Box
          pad={{ horizontal: "small", vertical: "xsmall" }}
          border={{ color: "border", side: "bottom", size: "xsmall" }}
          overflow="hidden"
          style={{
            display: "grid",
            gridTemplateColumns: SUMMARY_GRID_COLUMNS,
            columnGap: GRID_COLUMN_GAP,
            width: "100%",
            minWidth: 0,
            alignItems: "center",
          }}
        >
          <SortHeaderButton
            label="Candidate Name"
            column={SORT_COLUMN_CANDIDATE}
            sortDirection={sortDirection}
            onSort={handleHeaderSort}
          />
          <SortHeaderButton
            label="Organization"
            column={SORT_COLUMN_ORGANIZATION}
            sortDirection={sortDirection}
            onSort={handleHeaderSort}
          />
          <SortHeaderButton
            label="Date"
            column={SORT_COLUMN_DATE}
            align="end"
            sortDirection={sortDirection}
            onSort={handleHeaderSort}
          />
          <Text
            size="xsmall"
            weight="bold"
            color="text-weak"
            truncate
            textAlign="end"
          >
            Flag
          </Text>
          <Box flex={false} width={EXPAND_COLUMN_WIDTH} />
        </Box>
        {visibleSessions.map((session) => {
          const isExpanded = expandedId === session.podSessionId;
          const notesText = session.notesText.trim();

          return (
            <Box
              key={session.podSessionId}
              fill="horizontal"
              border={{ color: "border", side: "bottom", size: "xsmall" }}
              style={
                isExpanded
                  ? {
                      backgroundColor: getExpandedCardBackgroundColor(
                        session.flag,
                      ),
                      boxShadow: EXPANDED_SHADOW,
                    }
                  : undefined
              }
            >
              <Box pad={{ horizontal: "small" }} overflow="hidden">
                <SummaryColumns
                  session={session}
                  expandControl={renderExpandButton(session)}
                />
              </Box>
              {isExpanded && (
                <Box pad={{ horizontal: "small", bottom: "small" }} gap="small">
                  <ExpandedMeta
                    session={session}
                    claimedAt={claimedAtValue(session)}
                  />
                  <Box gap="xsmall">
                    <Box
                      direction="row"
                      align="center"
                      justify="between"
                      gap="small"
                      wrap={false}
                    >
                      <Text size="xsmall" color="text-weak">
                        Proctor Notes
                      </Text>
                      {session.googleMeetLink ? (
                        <Box flex={false}>
                          <Button
                            size="small"
                            icon={<Link size="small" />}
                            label="Open Meet"
                            a11yTitle="Open Meet"
                            href={session.googleMeetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        </Box>
                      ) : null}
                    </Box>
                    <Text size="small" style={{ whiteSpace: "pre-wrap" }}>
                      {notesText || "No notes available."}
                    </Text>
                  </Box>
                  <Box
                    overflow="hidden"
                    style={{
                      display: "grid",
                      gridTemplateColumns: SESSION_ID_GRID_COLUMNS,
                      columnGap: GRID_COLUMN_GAP,
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    <Box aria-hidden="true" />
                    <Text
                      size="xsmall"
                      color="text-weak"
                      truncate
                      textAlign="end"
                      title={`Session ID: ${session.podSessionId}`}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {`Session ID: ${session.podSessionId}`}
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box
      align="center"
      justify="start"
      pad="medium"
      fill="horizontal"
      overflow={{ horizontal: "hidden" }}
    >
      <Box
        fill="horizontal"
        pad={{ horizontal: "medium" }}
        gap="small"
        overflow={{ horizontal: "hidden" }}
      >
        <Heading level="3" size="small" margin="none">
          Session History
        </Heading>
        <Text size="small" color="text-weak">
          Review the sessions you have proctored during the last 90 days.
        </Text>
        {!loading && !searching && !error && sessions.length > 0 && (
          <Box
            direction="row"
            wrap
            gap="small"
            align="end"
            margin={{ top: "small" }}
          >
            <Box gap="xsmall" width="small">
              <Text size="small" color="dark-3">
                Filter by:
              </Text>
              <Select
                size="small"
                options={FILTER_TYPE_OPTIONS}
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                value={filterType}
                a11yTitle="Filter by"
                onChange={({ value }) => handleFilterTypeChange(value)}
              />
            </Box>
            <Box width="medium">
              <Select
                size="small"
                options={valueOptions}
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                value={
                  valueOptions.some((option) => option.value === filterValue)
                    ? filterValue
                    : FILTER_VALUE_ALL
                }
                a11yTitle={
                  filterType === FILTER_TYPE_FLAG
                    ? "Filter by flag"
                    : "Filter by property"
                }
                onChange={({ value }) => handleFilterValueChange(value)}
              />
            </Box>
            <Box gap="xsmall">
              <Text size="small" color="dark-3">
                Date Range
              </Text>
              <Box direction="row" gap="xsmall" align="center" wrap>
                <Box width="11rem">
                  <TextInput
                    type="date"
                    size="small"
                    value={dateStart}
                    a11yTitle="Start date"
                    onChange={(event) => handleDateStartChange(event.target.value)}
                  />
                </Box>
                <Box width="11rem">
                  <TextInput
                    type="date"
                    size="small"
                    value={dateEnd}
                    a11yTitle="End date"
                    onChange={(event) => handleDateEndChange(event.target.value)}
                  />
                </Box>
                <Button
                  size="small"
                  label="Clear Dates"
                  disabled={!dateStart && !dateEnd}
                  onClick={handleClearDates}
                />
                <Button
                  size="small"
                  label="Clear Filters"
                  disabled={isDefaultDynamicFilter(filterType, filterValue)}
                  onClick={handleClearFilters}
                />
              </Box>
            </Box>
          </Box>
        )}
        {SHOW_SESSION_STATISTICS_CARD &&
          !loading &&
          !searching &&
          !error &&
          sessions.length > 0 && (
            <SessionStatisticsTable
              filterType={filterType}
              filterValue={filterValue}
              statistics={sessionStatistics}
              setupDisplay={setupTimeStats.formatted}
              setupCoverage={setupTimeStats.coverageText}
              isSetupCalculating={isSetupCalculating}
              hasMore={hasMore}
            />
          )}
        <Box margin={{ top: "small" }} fill="horizontal">
          {renderBody()}
        </Box>
        {!loading &&
          !searching &&
          !error &&
          filteredAndSortedSessions.length > 0 && (
            <Box
              direction="row"
              wrap
              justify="between"
              align="center"
              gap="small"
              margin={{ top: "xsmall" }}
            >
              <Text size="small">{showingText}</Text>
              <Box direction="row" wrap align="center" gap="small">
                <Box direction="row" align="center" gap="xsmall">
                  <Text size="small">Sessions per page</Text>
                  <Box width="6rem">
                    <Select
                      size="small"
                      options={PAGE_SIZE_OPTIONS}
                      labelKey="label"
                      valueKey={{ key: "value", reduce: true }}
                      value={sessionsPerPage}
                      a11yTitle="Sessions per page"
                      onChange={({ value }) => handleSessionsPerPageChange(value)}
                    />
                  </Box>
                </Box>
                <Button
                  size="small"
                  label="Previous"
                  a11yTitle="Previous page"
                  disabled={safePage <= 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                />
                <Text size="small">
                  Page {safePage} of {pageCount}
                </Text>
                <Button
                  size="small"
                  label="Next"
                  a11yTitle="Next page"
                  disabled={safePage >= pageCount}
                  onClick={() =>
                    setCurrentPage((page) => Math.min(pageCount, page + 1))
                  }
                />
              </Box>
            </Box>
          )}
        {!loading && !searching && !error && hasMore && (
          <Box align="center" gap="small" margin={{ top: "small" }}>
            {loadMoreError && (
              <Text size="small" color="status-critical" textAlign="center">
                Unable to load more sessions.
              </Text>
            )}
            <Button
              label={
                sessions.length > 0 ? "Load More" : "Search Older Sessions"
              }
              disabled={loadingMore}
              onClick={() => loadSessions({ reset: false })}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default SessionHistory;
